import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { CarrinhoComprasProvider } from './contexts/CarrinhoComprasContext';
import { CarrinhoComprasLojaFisicaProvider } from './contexts/CarrinhoComprasLojaFisicaContext';
import Layout from './components/Layout/Layout';
import PrivateRoute from './components/PrivateRoute/PrivateRoute';
import AdminRoute from './components/AdminRoute/AdminRoute';
import CrmRoute from './components/CrmRoute/CrmRoute';
import Home from './pages/Home/Home';
import Login from './pages/Login/Login';
import Register from './pages/Register/Register';
import TrocarSenha from './pages/TrocarSenha/TrocarSenha';
import Users from './pages/Users/Users';
import EcommerceCatalog from './pages/Ecommerce/EcommerceCatalog';
import LojaFisicaCatalog from './pages/LojaFisica/LojaFisicaCatalog';
import LojaCentro from './pages/LojaFisica/LojaCentro';
import LojaCaxias from './pages/LojaFisica/LojaCaxias';
import RelatorioEntradasEcommerce from './pages/RelatorioEntradasEcommerce';
import ProductDetail from './pages/ProductDetail/ProductDetail';
import PicosQueda from './pages/PicosQueda/PicosQueda';
import RelatoriosPersonalizados from './pages/Relatorios/RelatoriosPersonalizados';
import AnalyticsDashboard from './pages/Analytics/AnalyticsDashboard';
import LogsViewer from './pages/Admin/LogsViewer';
import OpsDashboard from './pages/Admin/OpsDashboard/OpsDashboard';
import WhiteboardPage from './pages/Whiteboard/WhiteboardPage';
import Dashboard from './pages/Dashboard/Dashboard';
import PedidosMateriais from './pages/PedidosMateriais/PedidosMateriais';
import Clientes from './pages/Clientes/Clientes';
import Clientes360 from './pages/Clientes360/Clientes360';
import Limeira from './pages/Limeira/Limeira';
import LimeiraProdutos from './pages/Limeira/LimeiraProdutos';
import LimeiraFornecedor from './pages/Limeira/LimeiraFornecedor';
import LimeiraHistorico from './pages/Limeira/LimeiraHistorico';
import LimeiraConferencia from './pages/Limeira/LimeiraConferencia';

import RelatoriosFaturamento from './pages/RelatoriosFaturamento/RelatoriosFaturamento';
import DashboardBling from './pages/DashboardBling/DashboardBling';
import Macro from './pages/Macro/Macro';
import RelatoriosV2Consolidado from './pages/RelatoriosFaturamentoV2/Consolidado';
import RelatoriosV2LojaDetalhe from './pages/RelatoriosFaturamentoV2/LojaDetalhe';
import CampaignList from './pages/Campaigns/CampaignList';
import CampaignDetails from './pages/Campaigns/CampaignDetails';
import NotificationHistory from './pages/Notifications/NotificationHistory';
import ConferenciaEcommerce from './pages/ConferenciaEcommerce/ConferenciaEcommerce';
import HistoricoCompras from './pages/HistoricoCompras/HistoricoCompras';
import AprovacoesCompras from './pages/AprovacoesCompras/AprovacoesCompras';
import WhatsAppPage from './pages/WhatsApp/WhatsAppPage';
import ControleContatoClientes from './pages/ControleContatoClientes/ControleContatoClientes';
import CatalogoAdmin from './pages/CatalogoAdmin/CatalogoAdmin';
import ChatAssistant from './pages/IA/ChatAssistant';
import IAAnalytics from './pages/IA/IAAnalytics';
import AprovacoesComprasLojaFisica from './pages/AprovacoesComprasLojaFisica/AprovacoesComprasLojaFisica';
import HistoricoComprasLojaFisica from './pages/HistoricoComprasLojaFisica/HistoricoComprasLojaFisica';
import ChatPage from './pages/Chat/ChatPage';
import ChatHistoricoAdmin from './pages/Admin/ChatHistoricoAdmin/ChatHistoricoAdmin';
import ProdutosConsolidados from './pages/ProdutosConsolidados/ProdutosConsolidados';
import Octadesk from './pages/Octadesk';
import OctadeskRegras from './pages/OctadeskRegras/OctadeskRegras';
import Financeiro from './pages/Financeiro';
import CatalogoFornecedor from './pages/CatalogoFornecedor/CatalogoFornecedor';
const PDV = lazy(() => import('./pages/PDV/PDV'));
const Chamados      = lazy(() => import('./pages/Chamados/Chamados'));
const ChamadoDetalhe = lazy(() => import('./pages/Chamados/ChamadoDetalhe'));
// CRM Atendimento — lazy (evita inflar bundle inicial)
const CRMInbox       = lazy(() => import('./pages/CRM/Inbox'));
const CRMConfigCanais = lazy(() => import('./pages/CRM/ConfigCanais'));
const CRMSupervisao   = lazy(() => import('./pages/CRM/SupervisaoCRM'));
const PreCaixa = lazy(() => import('./pages/PDV/PreCaixa'));
const AdminProdutos = lazy(() => import('./pages/PDV/AdminProdutos'));
const Retaguarda = lazy(() => import('./pages/PDV/Retaguarda'));
const Relatorios = lazy(() => import('./pages/PDV/Relatorios'));
const AdminCupons = lazy(() => import('./pages/PDV/AdminCupons'));
const Crediario = lazy(() => import('./pages/PDV/Crediario'));
const Inventario = lazy(() => import('./pages/PDV/Inventario'));
const ConfigLoja = lazy(() => import('./pages/PDV/ConfigLoja'));
const AuditoriaPDV = lazy(() => import('./pages/PDV/Auditoria'));
const CaixasOperadores = lazy(() => import('./pages/PDV/CaixasOperadores'));
const Roadmap = lazy(() => import('./pages/Roadmap/Roadmap'));
// Catálogo público da Loja Centro (sem login) + painel interno de ocultos — lazy
const CatalogoPublico = lazy(() => import('./pages/Catalogo/CatalogoPublico'));
const CatalogoCliente = lazy(() => import('./pages/CatalogoCliente/CatalogoCliente'));
const ImprimirPedido = lazy(() => import('./pages/AdminCatalogoCentro/ImprimirPedido'));
import PDVErrorBoundary from './pages/PDV/PDVErrorBoundary';
import { useAuth } from './contexts/AuthContext';

// Guard do admin ROOT (só thiago@gmail.com) — usado pelo Roadmap.
// Backend valida o mesmo em routes/roadmap.js; isto evita render indevido.
function RootAdminRoute({ children }) {
  const { user, loading, isRootAdmin } = useAuth();
  if (loading) return <div style={{padding:40, fontFamily:'system-ui'}}>Verificando sessão...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isRootAdmin()) return <Navigate to="/" replace />;
  return children;
}

// Guard simplificado pro PDV — só checa auth, sem regras de fornecedor/troca-senha
// (o PrivateRoute padrão tem comportamento extra que estava bloqueando a renderização)
function PDVGuard({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{padding:40, fontFamily:'system-ui'}}>Verificando sessão...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Telas do PDV só de admin: quem não é admin volta ao /pdv (não sai do PDV).
// Barreira real é o backend (soAdmin em routes/pdvDispositivo.js).
function PDVAdminGuard({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return <div style={{padding:40, fontFamily:'system-ui'}}>Verificando sessão...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin()) return <Navigate to="/pdv" replace />;
  return children;
}

// Entrada do PDV (/pdv): NÃO redireciona sem login — a própria tela do PDV
// mostra o login do operador (e-mail + senha + loja). Assim o caixa é
// autossuficiente: o operador entra ali, sem passar pelo dashboard.
function PDVEntryGuard({ children }) {
  const { loading } = useAuth();
  if (loading) return <div style={{padding:40, fontFamily:'system-ui'}}>Verificando sessão...</div>;
  return children;
}
import './App.css';

// Configurar React Query — retry com backoff exponencial pra resiliência durante
// blip de restart do backend (até ~30s). Só retenta erros transitórios (5xx/network).
const isTransientHttpError = (err) => {
  const code = err?.response?.status || err?.status;
  if (!code) return true;            // network error / sem resposta = transitório
  return code >= 500 && code < 600;  // 5xx (502 Bad Gateway, 503, 504) = transitório
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (!isTransientHttpError(error)) return false;  // 4xx etc: não retenta
        return failureCount < 3;                          // até 3 retries em 5xx/network
      },
      retryDelay: (attempt) => {
        // backoff [1s, 3s, 5s] + jitter ±300ms — total max ~9s, cobre blip <10s
        const base = [1000, 3000, 5000][Math.min(attempt, 2)] || 5000;
        return base + (Math.random() * 600 - 300);
      },
      staleTime: 5 * 60 * 1000, // 5 minutos
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <CarrinhoComprasProvider>
          <CarrinhoComprasLojaFisicaProvider>
          <Routes>
            {/* Rotas públicas */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            {/* Catálogo público da Loja Centro — link enviado aos clientes, sem login/menu.
                (/catalogo/* é outra SPA servida direto pelo nginx, por isso /loja/centro) */}
            <Route
              path="/loja/centro"
              element={
                <Suspense fallback={<div style={{ padding: 40, fontFamily: 'system-ui', color: '#94a3b8' }}>Carregando catálogo...</div>}>
                  <CatalogoPublico apiBase="/api/catalogo-fisica/centro" />
                </Suspense>
              }
            />

            {/* Rota de troca de senha obrigatória (sem layout) */}
            <Route path="/trocar-senha" element={<TrocarSenha />} />

            {/* PDV — página standalone (sem Layout/menu), protegida + lazy */}
            <Route
              path="/pdv"
              element={
                <PDVEntryGuard>
                  <PDVErrorBoundary>
                    <Suspense fallback={
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                        Carregando PDV...
                      </div>
                    }>
                      <PDV />
                    </Suspense>
                  </PDVErrorBoundary>
                </PDVEntryGuard>
              }
            />
            <Route
              path="/precaixa"
              element={
                <Suspense fallback={
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                    Carregando pré-caixa...
                  </div>
                }>
                  <PreCaixa />
                </Suspense>
              }
            />
            <Route
              path="/pdv/produtos"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <AdminProdutos />
                  </Suspense>
                </PDVGuard>
              }
            />
            <Route
              path="/pdv/retaguarda"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <Retaguarda />
                  </Suspense>
                </PDVGuard>
              }
            />
            <Route
              path="/pdv/relatorios"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <Relatorios />
                  </Suspense>
                </PDVGuard>
              }
            />
            <Route
              path="/pdv/cupons"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <AdminCupons />
                  </Suspense>
                </PDVGuard>
              }
            />
            <Route
              path="/pdv/crediario"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <Crediario />
                  </Suspense>
                </PDVGuard>
              }
            />
            <Route
              path="/pdv/inventario"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <Inventario />
                  </Suspense>
                </PDVGuard>
              }
            />
            <Route
              path="/pdv/auditoria"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <AuditoriaPDV />
                  </Suspense>
                </PDVGuard>
              }
            />
            <Route
              path="/pdv/caixas"
              element={
                <PDVAdminGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <CaixasOperadores />
                  </Suspense>
                </PDVAdminGuard>
              }
            />
            <Route
              path="/pdv/config"
              element={
                <PDVGuard>
                  <Suspense fallback={
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', fontFamily:'system-ui', color:'#94a3b8' }}>
                      Carregando...
                    </div>
                  }>
                    <ConfigLoja />
                  </Suspense>
                </PDVGuard>
              }
            />

            {/* Rotas protegidas - Admin apenas */}
            <Route
              path="/"
              element={
                <AdminRoute>
                  <Layout>
                    <Home />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/ecommerce"
              element={
                <PrivateRoute>
                  <Layout>
                    <EcommerceCatalog />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/catalogo-cliente"
              element={
                <PrivateRoute>
                  <Layout>
                    <Suspense fallback={<div style={{ padding: 40 }}>Carregando...</div>}>
                      <CatalogoCliente />
                    </Suspense>
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Folha A4 do pedido (sem menu, para imprimir) - pedidos do catalogo e do CRM */}
            <Route
              path="/catalogo-cliente/pedidos/:id/imprimir"
              element={
                <PrivateRoute>
                  <Suspense fallback={<div style={{ padding: 40 }}>Carregando...</div>}>
                    <ImprimirPedido />
                  </Suspense>
                </PrivateRoute>
              }
            />
            {/* Endereços antigos do catálogo da Centro → Catálogo Cliente (tudo num lugar só, 2026-10-13) */}
            <Route path="/loja-fisica/centro/catalogo-publico" element={<Navigate to="/catalogo-cliente" replace />} />
            <Route
              path="/catalogo-admin"
              element={
                <PrivateRoute>
                  <Layout>
                    <CatalogoAdmin />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/roadmap"
              element={
                <RootAdminRoute>
                  <Layout>
                    <Suspense fallback={<div style={{ padding: 40, fontFamily: 'system-ui', color: '#94a3b8' }}>Carregando roadmap…</div>}>
                      <Roadmap />
                    </Suspense>
                  </Layout>
                </RootAdminRoute>
              }
            />
            <Route
              path="/loja-fisica"
              element={
                <PrivateRoute>
                  <Layout>
                    <LojaFisicaCatalog />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/loja-fisica/centro"
              element={
                <PrivateRoute>
                  <Layout>
                    <LojaCentro />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/loja-fisica/caxias"
              element={
                <PrivateRoute>
                  <Layout>
                    <LojaCaxias />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/loja-fisica/aprovacao-compras"
              element={
                <PrivateRoute>
                  <Layout>
                    <AprovacoesComprasLojaFisica />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/loja-fisica/historico-compras"
              element={
                <PrivateRoute>
                  <Layout>
                    <HistoricoComprasLojaFisica />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/ecommerce/entradas"
              element={
                <PrivateRoute>
                  <Layout>
                    <RelatorioEntradasEcommerce />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/ecommerce/conferencia"
              element={
                <PrivateRoute>
                  <Layout>
                    <ConferenciaEcommerce />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/ecommerce/historico-compras"
              element={
                <PrivateRoute>
                  <Layout>
                    <HistoricoCompras />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/ecommerce/aprovacao-compras"
              element={
                <PrivateRoute>
                  <Layout>
                    <AprovacoesCompras />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/mapa-de-calor"
              element={
                <PrivateRoute>
                  <Layout>
                    <AnalyticsDashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/picos-queda"
              element={
                <PrivateRoute>
                  <Layout>
                    <PicosQueda />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/pedidos-materiais"
              element={
                <PrivateRoute>
                  <Layout>
                    <PedidosMateriais />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/:tipo/:codigo"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProductDetail />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/usuarios"
              element={
                <PrivateRoute>
                  <Layout>
                    <Users />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/notifications-history"
              element={
                <PrivateRoute>
                  <Layout>
                    <NotificationHistory />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/catalogo-centro"
              element={<Navigate to="/catalogo-cliente?aba=pedidos" replace />}
            />
            <Route
              path="/admin/logs"
              element={
                <PrivateRoute>
                  <Layout>
                    <LogsViewer />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/ops"
              element={
                <AdminRoute>
                  <Layout>
                    <OpsDashboard />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/chat-historico"
              element={
                <AdminRoute>
                  <Layout>
                    <ChatHistoricoAdmin />
                  </Layout>
                </AdminRoute>
              }
            />
            <Route
              path="/clientes"
              element={
                <PrivateRoute>
                  <Layout>
                    <Clientes />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/clientes-360"
              element={
                <PrivateRoute>
                  <Layout>
                    <Clientes360 />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/limeira/produtos"
              element={
                <PrivateRoute>
                  <Layout>
                    <LimeiraProdutos />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/limeira/fornecedor"
              element={
                <PrivateRoute>
                  <Layout>
                    <LimeiraFornecedor />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/limeira/conferencia"
              element={
                <PrivateRoute>
                  <Layout>
                    <LimeiraConferencia />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Quadro Branco Infinito */}
            <Route path="/quadro-branco" element={
              <PrivateRoute>
                <Layout>
                  <WhiteboardPage />
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/quadro-branco/:boardId" element={
              <PrivateRoute>
                <Layout>
                  <WhiteboardPage />
                </Layout>
              </PrivateRoute>
            } />

            {/* Chat Interno do Time */}
            <Route path="/chat" element={
              <PrivateRoute>
                <Layout>
                  <ChatPage />
                </Layout>
              </PrivateRoute>
            } />

            {/* Octadesk — Atendimento WhatsApp */}
            <Route path="/octadesk" element={
              <PrivateRoute>
                <Layout>
                  <Octadesk />
                </Layout>
              </PrivateRoute>
            } />
            <Route path="/octadesk/regras" element={
              <PrivateRoute>
                <Layout>
                  <OctadeskRegras />
                </Layout>
              </PrivateRoute>
            } />

            {/* Financeiro — Dados direto da API Bling */}
            <Route path="/financeiro" element={
              <PrivateRoute>
                <Layout>
                  <Financeiro />
                </Layout>
              </PrivateRoute>
            } />

            {/* Análise de Catálogos de Fornecedores */}
            <Route
              path="/catalogo-fornecedor"
              element={
                <PrivateRoute>
                  <Layout>
                    <CatalogoFornecedor />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* Assistente IA */}
            <Route
              path="/assistente-ia"
              element={
                <PrivateRoute>
                  <Layout>
                    <ChatAssistant />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/assistente-ia/analytics"
              element={
                <AdminRoute>
                  <Layout>
                    <IAAnalytics />
                  </Layout>
                </AdminRoute>
              }
            />

            {/* Rotas de Admin */}
            <Route
              path="/limeira"
              element={
                <PrivateRoute>
                  <Layout>
                    <Limeira />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/limeira/historico"
              element={
                <PrivateRoute>
                  <Layout>
                    <LimeiraHistorico />
                  </Layout>
                </PrivateRoute>
              }
            />
            {/* Produtos Consolidados - visão unificada das 3 lojas */}
            <Route
              path="/produtos-consolidados"
              element={
                <PrivateRoute>
                  <Layout>
                    <ProdutosConsolidados />
                  </Layout>
                </PrivateRoute>
              }
            />

            <Route
              path="/relatorios-faturamento"
              element={
                <PrivateRoute>
                  <Layout>
                    <RelatoriosFaturamento />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard-bling"
              element={
                <PrivateRoute>
                  <Layout>
                    <DashboardBling />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/macro"
              element={
                <PrivateRoute>
                  <Layout>
                    <Macro />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/relatorios-faturamento-v2"
              element={
                <PrivateRoute>
                  <Layout>
                    <RelatoriosV2Consolidado />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/relatorios-faturamento-v2/:loja"
              element={
                <PrivateRoute>
                  <Layout>
                    <RelatoriosV2LojaDetalhe />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/clientes-inativos"
              element={
                <PrivateRoute>
                  <Layout>
                    <DashboardBling defaultTab="clientes" />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/controle-contato-clientes"
              element={
                <PrivateRoute>
                  <Layout>
                    <ControleContatoClientes />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/campanhas"
              element={
                <PrivateRoute>
                  <Layout>
                    <CampaignList />
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard/campanhas/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <CampaignDetails />
                  </Layout>
                </PrivateRoute>
              }
            />

            {/* ── CRM Atendimento (acesso restrito via CrmRoute) ── */}
            <Route
              path="/crm"
              element={
                <CrmRoute>
                  <Layout>
                    <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>}>
                      <CRMInbox />
                    </Suspense>
                  </Layout>
                </CrmRoute>
              }
            />
            <Route
              path="/crm/canais"
              element={
                <CrmRoute>
                  <Layout>
                    <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>}>
                      <CRMConfigCanais />
                    </Suspense>
                  </Layout>
                </CrmRoute>
              }
            />
            <Route
              path="/crm/supervisao"
              element={
                <CrmRoute>
                  <Layout>
                    <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>}>
                      <CRMSupervisao />
                    </Suspense>
                  </Layout>
                </CrmRoute>
              }
            />

            {/* Chamados — listagem e detalhe */}
            <Route
              path="/chamados"
              element={
                <PrivateRoute>
                  <Layout>
                    <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>}>
                      <Chamados />
                    </Suspense>
                  </Layout>
                </PrivateRoute>
              }
            />
            <Route
              path="/chamados/:id"
              element={
                <PrivateRoute>
                  <Layout>
                    <Suspense fallback={<div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Carregando...</span></div></div>}>
                      <ChamadoDetalhe />
                    </Suspense>
                  </Layout>
                </PrivateRoute>
              }
            />
          </Routes>
          </CarrinhoComprasLojaFisicaProvider>
          </CarrinhoComprasProvider>
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
